import React, { useState, useEffect } from 'react';
import { Table, Input, Select, DatePicker, Button, Space, message, Modal, Tag, Popconfirm } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, UndoOutlined } from '@ant-design/icons';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

// 兜底列表：接口失败时使用，保持与后端实际使用的 action 一致
const FALLBACK_ACTIONS = [
  '快速编辑', '修改状态', '批量改状态', '批量改字段', '快速导入', '删除订单', '新增订单',
  '编辑订单', '批量改粉丝', '批量复制', '导出全部订单', 'Excel导入', '导出选中订单', '批量分配',
  '巨量同步授权状态', '拉取粉丝数',
  '新增账单', '批量修改账单', '批量导入账单', '导出账单',
  '新增参考价格', '修改参考价格', '删除参考价格',
  '新增监控Cookie', '回滚操作'
];

const FIELD_LABELS = {
  supplier_id: '主体', filter_use: '筛选使用', platform: '平台',
  handler: '受理员工', team: '小组', period: '期数', group_name: '群名',
  order_time: '下单时间', contact_person: '对接人', growth_method: '涨粉方式',
  account_name: '视频号名称/抖音号', text3: '文本3', link_type: '链接属性',
  exclusive_link: '专属链接/涨粉链接', account_duplicate: '账号重复',
  method_duplicate: '方式+账号重复', initial_fans: '初始粉丝数',
  final_fans: '涨粉后粉丝数', growth_count: '涨粉量',
  pre_growth_text: '涨粉前截图文字', post_growth_text: '涨粉完结截图文字',
  status_id: '完结状态', complete_date: '完成日期', remark: '备注',
  text1: '文本1', text2: '文本2', reference_price: '参考价格',
  actual_price: '实际价格', modified_by_name: '最新修改人', id: 'ID', created_at: '创建时间', updated_at: '更新时间',
  supplier_name: '主体名称', old_status_id: '原状态ID', old_value: '原值',
  account_name: '抖音号', copied_count: '复制数量',
  status_name: '状态名称', rollback_log_id: '回滚日志ID', count: '数量',
  field: '字段名', value: '值', orders: '订单列表'
};

function OperationLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({});
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLog, setDetailLog] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [statusMap, setStatusMap] = useState({});
  const [actionOptions, setActionOptions] = useState(
    FALLBACK_ACTIONS.map(a => ({ label: a, value: a }))
  );

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, pageSize]);

  // 加载操作类型（从日志实时汇总，新增类型自动出现）
  useEffect(() => {
    api.get('/operation-logs/actions').then(res => {
      if (res && res.code === 200 && Array.isArray(res.data) && res.data.length > 0) {
        setActionOptions(res.data.map(r => ({
          label: `${r.action}（${r.count}）`,
          value: r.action
        })));
      }
    }).catch(() => {});
  }, []);

  // 加载状态映射（用于把日志里的状态ID翻译成名称）
  useEffect(() => {
    api.get('/statuses').then(res => {
      if (res.code === 200) {
        const map = {};
        (res.data || []).forEach(st => { map[st.id] = st.name; });
        setStatusMap(map);
      }
    }).catch(() => {});
  }, []);

  // 把详情文本中的状态ID翻译成名称（兼容历史日志）
  const formatDetail = (text) => {
    if (!text) return text;
    const idName = (id) => (statusMap[id] !== undefined ? statusMap[id] : id);
    return String(text)
      .replace(/状态从 (\d+) 改为 (\d+)/g, (m, a, b) => `状态从「${idName(a)}」改为「${idName(b)}」`)
      .replace(/状态ID改为 (\d+)/g, (m, a) => `状态改为「${idName(a)}」`)
      .replace(/字段 status_id 从 "(\d+)" 改为 "(\d+)"/g, (m, a, b) => `字段 完结状态 从「${idName(a)}」改为「${idName(b)}」`)
      .replace(/字段 status_id 改为 "(\d+)"/g, (m, a) => `字段 完结状态 改为「${idName(a)}」`);
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, pageSize, ...filters };
      if (params.dateRange) {
        params.start_date = params.dateRange[0]?.format('YYYY-MM-DD');
        params.end_date = params.dateRange[1]?.format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const res = await api.get('/operation-logs', { params });
      if (res.code === 200) {
        setLogs(res.data.list);
        setTotal(res.data.total);
      }
    } catch (err) {
      message.error('加载日志失败');
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setPage(1);
    loadLogs();
  };

  const parseJsonData = (data) => {
    if (!data) return null;
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return null; }
    }
    return data;
  };

  const showDetail = (record) => {
    setDetailLog(record);
    setDetailVisible(true);
  };

  const handleRollback = async (record) => {
    setRollbackLoading(true);
    try {
      const res = await api.post(`/operation-logs/${record.id}/rollback`);
      if (res.code === 200) {
        message.success(res.message);
        loadLogs();
      }
    } catch (err) {
      message.error(err?.response?.data?.message || '回滚失败');
    }
    setRollbackLoading(false);
  };

  // 构建对比数据
  const buildCompareData = () => {
    if (!detailLog) return [];
    const oldData = parseJsonData(detailLog.old_data);
    const newData = parseJsonData(detailLog.new_data);

    if (!oldData && !newData) return [];

    const mapStatusVal = (key, val) => {
      if ((key === 'status_id' || key === 'old_status_id') && val !== null && val !== undefined) {
        return statusMap[val] !== undefined ? statusMap[val] : val;
      }
      return val;
    };

    // 批量操作（含 orders 数组）：展开成「订单ID + 抖音号 + 字段 + 修改前/后」的平铺行
    if (oldData?.orders && Array.isArray(oldData.orders)) {
      const rows = [];
      const newOrders = Array.isArray(newData?.orders) ? newData.orders : null;
      const batchField = newData && newData.field;
      const batchValue = newData && newData.value;
      const batchStatusId = newData && newData.status_id;
      oldData.orders.forEach((item, idx) => {
        const newItem = newOrders ? (newOrders[idx] || {}) : null;
        Object.keys(item).forEach((f, j) => {
          if (f === 'id' || f === 'account_name') return;
          // 新值来源：逐条 orders → 批量字段值 → 批量目标状态
          let newVal;
          if (newItem && newItem[f] !== undefined) {
            newVal = mapStatusVal(f, newItem[f]);
          } else if (batchField === f && batchValue !== undefined) {
            newVal = mapStatusVal(f, batchValue);
          } else if (f === 'status_id' && batchStatusId !== undefined) {
            newVal = mapStatusVal(f, batchStatusId);
          }
          if (newVal === undefined) return;
          const oldVal = mapStatusVal(f, item[f]);
          if (oldVal === newVal) return;
          rows.push({
            key: `${idx}-${f}-${j}`,
            orderId: item.id,
            accountName: item.account_name,
            fieldKey: f,
            field: FIELD_LABELS[f] || f,
            oldVal,
            newVal
          });
        });
      });
      return rows;
    }

    // 批量改粉丝：newData.entries = [{id, value}]
    if (Array.isArray(newData?.entries)) {
      return newData.entries.map((e, i) => ({
        key: `entry-${i}`,
        orderId: e.id,
        accountName: '',
        fieldKey: newData.field,
        field: FIELD_LABELS[newData.field] || newData.field || '字段',
        oldVal: '—',
        newVal: e.value
      }));
    }

    // 新增类操作（如快速导入）：newData.created 是本次创建的订单
    if (Array.isArray(newData?.created)) {
      return newData.created.map((c, i) => ({
        key: `created-${i}`,
        orderId: c.id,
        accountName: c.account_name,
        fieldKey: 'created',
        field: '新增订单',
        oldVal: '—',
        newVal: '已创建'
      }));
    }

    // 单条操作
    const allKeys = new Set([
      ...Object.keys(oldData || {}),
      ...Object.keys(newData || {})
    ]);

    const rows = Array.from(allKeys)
      .filter(key => key !== 'id' && key !== 'created_at' && key !== 'updated_at')
      .map(key => ({
        key,
        field: FIELD_LABELS[key] || key,
        fieldKey: key,
        oldVal: mapStatusVal(key, oldData?.[key]),
        newVal: mapStatusVal(key, newData?.[key])
      }))
      .filter(item => {
        // 对于批量改字段，newData 里有 field/value 字段
        if (newData?.field && item.fieldKey === newData.field) {
          item.newVal = newData.value;
        }
        return item.oldVal !== item.newVal && item.oldVal !== undefined;
      });

    if (rows.length > 0) return rows;

    // 兜底：值看起来相同（空格/不可见字符差异）时，仍然把有数据的字段列出来
    return Array.from(allKeys)
      .filter(key => key !== 'id' && key !== 'created_at' && key !== 'updated_at')
      .filter(key => oldData?.[key] !== undefined || newData?.[key] !== undefined)
      .map(key => ({
        key: `fb-${key}`,
        field: FIELD_LABELS[key] || key,
        fieldKey: key,
        oldVal: mapStatusVal(key, oldData?.[key]),
        newVal: mapStatusVal(key, newData?.[key])
      }));
  };

  const compareData = buildCompareData();
  const hasBatchRows = compareData.some(r => r.orderId !== undefined);

  // 提取抖音号：从 old_data/new_data 中获取，或从 detail 文本中匹配
  const getAccountName = (record) => {
    const oldData = parseJsonData(record.old_data);
    const newData = parseJsonData(record.new_data);
    if (oldData?.account_name) return oldData.account_name;
    if (newData?.account_name) return newData.account_name;
    // 批量操作：从 orders 数组中提取
    if (oldData?.orders && Array.isArray(oldData.orders)) {
      const names = oldData.orders.map(o => o.account_name).filter(Boolean);
      if (names.length > 0) return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} 等${names.length}条`;
    }
    // 从 detail 文本中匹配
    const match = record.detail?.match(/（([^）]+)）/);
    return match ? match[1] : '-';
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '操作人', dataIndex: 'user_name', width: 100 },
    { title: '操作类型', dataIndex: 'action', width: 120 },
    { title: '目标类型', dataIndex: 'target_type', width: 80 },
    { title: '抖音号', key: 'account_name', width: 150, ellipsis: true, render: (_, record) => getAccountName(record) },
    { title: '目标ID', dataIndex: 'target_id', width: 120, ellipsis: true },
    { title: '详情', dataIndex: 'detail', ellipsis: true, render: v => formatDetail(v) },
    { title: 'IP', dataIndex: 'ip', width: 120 },
    { title: '操作时间', dataIndex: 'created_at', width: 170,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : ''
    },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space>
          {(record.old_data || record.new_data) && (
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>
              详情
            </Button>
          )}
          {user?.username === 'admin' && record.old_data && record.action !== '回滚操作' && (
            <Popconfirm title="确定回滚此操作？" onConfirm={() => handleRollback(record)}>
              <Button type="link" size="small" danger icon={<UndoOutlined />} loading={rollbackLoading}>
                回滚
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <h3>操作日志</h3>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="操作类型"
          style={{ width: 140 }}
          onChange={v => setFilters(f => ({ ...f, action: v }))}
          showSearch
          optionFilterProp="label"
          options={actionOptions}
        />
        <Input
          placeholder="操作人"
          style={{ width: 120 }}
          onChange={e => setFilters(f => ({ ...f, user_name: e.target.value }))}
          onPressEnter={handleSearch}
        />
        <Input
          placeholder="抖音号"
          style={{ width: 140 }}
          onChange={e => setFilters(f => ({ ...f, account_name: e.target.value }))}
          onPressEnter={handleSearch}
        />
        <RangePicker
          onChange={v => setFilters(f => ({ ...f, dateRange: v }))}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
        <Button icon={<ReloadOutlined />} onClick={() => { setFilters({}); setPage(1); setTimeout(loadLogs, 100); }}>重置</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
      />

      {/* 详情对比弹窗 */}
      <Modal
        title={`操作详情 #${detailLog?.id} - ${detailLog?.action}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {detailLog && (
          <>
            <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
              <span>操作人: {detailLog.user_name}</span>
              <span style={{ marginLeft: 16 }}>时间: {detailLog.created_at ? dayjs(detailLog.created_at).format('YYYY-MM-DD HH:mm:ss') : ''}</span>
              <span style={{ marginLeft: 16 }}>目标: {detailLog.target_type} #{detailLog.target_id}</span>
              {getAccountName(detailLog) !== '-' && (
                <span style={{ marginLeft: 16 }}>抖音号: {getAccountName(detailLog)}</span>
              )}
            </div>
            <div style={{
              marginBottom: 8, fontSize: 13, whiteSpace: 'pre-wrap',
              maxHeight: 160, overflow: 'auto', background: '#fafafa',
              padding: '8px 10px', borderRadius: 6
            }}>{
              // 有对比数据时只显示汇总（明细在下方表格里），否则显示完整文本
              compareData.length > 0
                ? formatDetail(String(detailLog.detail || '').split('变更明细：')[0].trim())
                : formatDetail(detailLog.detail)
            }</div>

            {compareData.length > 0 ? (
              <Table
                rowKey="key"
                columns={[
                  ...(hasBatchRows ? [
                    { title: '订单ID', dataIndex: 'orderId', width: 90 },
                    { title: '抖音号', dataIndex: 'accountName', width: 130, render: v => v || '-' }
                  ] : []),
                  { title: '字段', dataIndex: 'field', width: 140 },
                  { title: '修改前', dataIndex: 'oldVal', render: v => <span style={{ color: '#ff4d4f', whiteSpace: 'pre-wrap' }}>{v ?? '-'}</span> },
                  { title: '修改后', dataIndex: 'newVal', render: v => <span style={{ color: '#52c41a', whiteSpace: 'pre-wrap' }}>{v ?? '-'}</span> }
                ]}
                dataSource={compareData}
                pagination={false}
                size="small"
                scroll={{ y: 400 }}
              />
            ) : (
              <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>无对比数据</div>
            )}
            {compareData.length > 0 && compareData.every(r => String(r.oldVal ?? '') === String(r.newVal ?? '')) && (
              <div className="hint" style={{ marginTop: 8, color: '#d48806' }}>
                这条日志的前后值相同（保存时没有实际改动），属于历史记录；新版本已不再记录这种空操作
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

export default OperationLog;
